// Fact Sheet Section V2 - Optimized 2-Section Layout
// Section 1: Key Specifications (KPI + Mechanical + Chemical + Quality)
// Section 2: Performance Characteristics (Electrical + Plots + Thermal)

import { useEffect, useState } from 'preact/hooks'
import { FACTSHEET, renderDefinition } from '../../../data/definitions.js'
import { getCellPicture } from '../../../modules/dataService.js'
import { getGlobalStats } from '../../../modules/globalStats.js'
import { getTilesForSection } from '../../../modules/tileDataGenerator.jsx'
import { basis, domain } from '../../../state/globalSignals.js'
import { formatValue } from '../../../utils/formatUtils.js'
import ChemistryDiagram from '../../visualizations/ChemistryDiagram.jsx'
import DischargeRateCharacteristics from '../../visualizations/DischargeRateCharacteristics.jsx'
import RadarChart from '../../visualizations/RadarChart.jsx'

// SI unit conversions for display
const jPerKgToWhPerKg = (jkg) => jkg != null ? jkg / 3600 : null

export default function FactSheetSection({ cellData, imageUrl }) {
  // State for KPI tiles
  const [tiles, setTiles] = useState([])
  const [tilesLoading, setTilesLoading] = useState(true)
  const [flippedCards, setFlippedCards] = useState({})
  const [mechanicalImageUrl, setMechanicalImageUrl] = useState(null)
  const [mechanicalImageError, setMechanicalImageError] = useState(false)
  const [globalStats, setGlobalStats] = useState({})

  // Load global stats for radar chart axis ranges
  useEffect(() => {
    getGlobalStats().then(stats => {
      // Convert SI units to display units for definitions
      setGlobalStats({
        // Energy: J/kg → Wh/kg
        maxGravEnergyDensity: jPerKgToWhPerKg(stats?.energy?.grav?.max),
        // Power: W/kg stays W/kg
        maxGravPowerDensity: stats?.power?.grav?.max,
        // Batch variation and aging: fractions (0-1)
        maxCapacityVariation: stats?.batchVariation?.max,
        maxCapacityLoss: stats?.initialAging?.max
      })
    }).catch(err => {
      console.warn('[FactSheet] Failed to load global stats:', err)
    })
  }, [])

  // Load tiles initially and when basis/domain changes
  useEffect(() => {
    if (!cellData) return

    setTilesLoading(true)
    getTilesForSection('scores', cellData)
      .then(setTiles)
      .catch(err => {
        console.error('Failed to load tiles:', err)
        setTiles([])
      })
      .finally(() => setTilesLoading(false))
  }, [cellData, basis.value, domain.value])

  // Load mechanical virtual picture
  useEffect(() => {
    if (!cellData?.id) return

    let cancelled = false

    // Reset per-cell so a previously-failed cell doesn't leak its error/URL when
    // navigating to another cell.
    setMechanicalImageUrl(null)
    setMechanicalImageError(false)

    const loadMechanicalImage = async () => {
      try {
        const dataUrl = await getCellPicture(cellData.id, 'website_picture_virtual.png')
        if (cancelled) return
        if (dataUrl) {
          setMechanicalImageUrl(dataUrl)
        } else {
          // getCellPicture returns null on 403/404/error — surface a calm
          // "not available" instead of hanging on "Loading…" forever.
          setMechanicalImageError(true)
        }
      } catch (err) {
        console.warn('[FactSheet] Failed to load mechanical image:', err)
        if (!cancelled) setMechanicalImageError(true)
      }
    }

    loadMechanicalImage()

    return () => {
      cancelled = true
    }
  }, [cellData?.id])

  if (!cellData) {
    return (
      <div className="section-container section-fact-sheet">
        <div className="section-description">
          <p>Loading comprehensive cell specifications...</p>
        </div>
      </div>
    )
  }

  const cellSlug = cellData.id || ''
  const elec = cellData.electrical || {}
  const mech = cellData.mechanical || {}
  const chem = cellData.chemical || {}
  const batch = cellData.batch || {}
  const aging = cellData.aging || {}
  const thermal = cellData.thermal || {}
  const general = cellData.general || {}

  // Determine ion type based on technology (API returns "Li-Ion" or "Na-Ion")
  // Note: technology is at top-level in normalized data, not under general
  const ionLabel = cellData.technology?.includes('Na') ? 'Na⁺' : 'Li⁺'

  // Get current basis and domain
  const currentBasis = basis.value
  const currentDomain = domain.value

  // Calculate basis factors
  const m_kg = mech.mass_g ? mech.mass_g / 1000 : 1
  const vol_L = mech.vol_cell_cm3 ? mech.vol_cell_cm3 / 1000 : 1

  const getBasisFactor = () => {
    if (currentBasis === 'grav') return m_kg
    if (currentBasis === 'vol') return vol_L
    return 1
  }

  const getBasisUnit = (baseUnit) => {
    if (currentBasis === 'grav') return `${baseUnit}/kg`
    if (currentBasis === 'vol') return `${baseUnit}/L`
    return baseUnit
  }

  const basisFactor = getBasisFactor()
  const isDomainEnergyPower = currentDomain === 'energy-power'

  // Dynamic values
  const xAxisValues = isDomainEnergyPower
    ? [
        elec.E_c10_Wh ? formatValue(elec.E_c10_Wh / basisFactor, getBasisUnit('Wh')) : '–',
        elec.E_grav_Wh_per_kg && currentBasis === 'abs' ? formatValue(elec.E_grav_Wh_per_kg, 'Wh/kg') : '–'
      ]
    : [
        elec.C_nom_Ah ? formatValue(elec.C_nom_Ah / basisFactor, getBasisUnit('Ah')) : '–',
        elec.C_c10_Ah ? formatValue(elec.C_c10_Ah / basisFactor, getBasisUnit('Ah')) : '–'
      ]

  const yAxisValues = isDomainEnergyPower
    ? [
        elec.P_peak_W ? formatValue(elec.P_peak_W / basisFactor, getBasisUnit('W')) : '–',
        elec.P_cont_W ? formatValue(elec.P_cont_W / basisFactor, getBasisUnit('W')) : '–'
      ]
    : [
        elec.I_peak_A ? formatValue(elec.I_peak_A / basisFactor, getBasisUnit('A')) : '–',
        elec.I_cont_A ? formatValue(elec.I_cont_A / basisFactor, getBasisUnit('A')) : '–'
      ]

  const combinedFormat = mech.format || '–'
  const dimensionsString = mech.dimensions_text || null

  const toggleFlip = (cardId) => {
    setFlippedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }))
  }

  return (
    <div className="section-container section-fact-sheet-v2">

      {/* ========== SECTION 1: KEY SPECIFICATIONS ========== */}
      <div className="fact-sheet-section fact-sheet-key-specs">
        <div className="key-specs-grid">
          {/* Column 1: Scores */}
          <div className={`key-spec-column kpi-column flipcard ${flippedCards['kpi'] ? 'flipped' : ''}`}>
            <div className="flipcard-inner">
              <div className="flipcard-front">
                <button className="flip-btn" onClick={() => toggleFlip('kpi')} title="Show info">?</button>
                <h3 className="column-title">Scores</h3>
                <div className="radar-container-compact">
                  <RadarChart cellData={cellData} />
                </div>
                <div style="display:flex;gap:8px;margin-top:12px;padding:0 8px;">
                  <div style="flex:1;padding:8px 12px;background:rgba(50,50,50,0.4);backdrop-filter:blur(10px);border:1px solid rgba(234,91,12,0.2);border-radius:6px;display:flex;align-items:center;gap:8px;">
                    <div style="width:20px;height:3px;background:#ea5b0c;border-radius:2px;flex-shrink:0;"></div>
                    <span style="font-family:'Roboto',sans-serif;font-size:13px;color:rgba(255,255,255,0.6);font-weight:400;">Cell Value</span>
                  </div>
                  <div style="flex:1;padding:8px 12px;background:rgba(50,50,50,0.4);backdrop-filter:blur(10px);border:1px solid rgba(100,116,139,0.2);border-radius:6px;display:flex;align-items:center;gap:8px;">
                    <div style="width:20px;height:0;border-top:2px dashed #64748b;flex-shrink:0;"></div>
                    <span style="font-family:'Roboto',sans-serif;font-size:13px;color:rgba(255,255,255,0.6);font-weight:400;">Global Average</span>
                  </div>
                </div>
              </div>
              <div className="flipcard-back">
                <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('kpi')} title="Close">×</button>
                <h3 className="column-title">{FACTSHEET.scores.title}</h3>
                <div className="flip-content">
                  <p style="margin-bottom: 12px;">{FACTSHEET.scores.intro}</p>

                  <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.scores.energy.title}</p>
                  <p style="font-size: 11px; line-height: 1.4; margin-bottom: 10px;">
                    {renderDefinition(FACTSHEET.scores.energy.text, cellData, globalStats)}
                  </p>

                  <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.scores.power.title}</p>
                  <p style="font-size: 11px; line-height: 1.4; margin-bottom: 10px;">
                    {renderDefinition(FACTSHEET.scores.power.text, cellData, globalStats)}
                  </p>

                  <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.scores.batchVariation.title}</p>
                  <p style="font-size: 11px; line-height: 1.4; margin-bottom: 10px;">
                    {renderDefinition(FACTSHEET.scores.batchVariation.text, cellData, globalStats)}
                  </p>

                  <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.scores.initialAging.title}</p>
                  <p style="font-size: 11px; line-height: 1.4;">
                    {renderDefinition(FACTSHEET.scores.initialAging.text, cellData, globalStats)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: Mechanical */}
          <div className={`key-spec-column mechanical-column flipcard ${flippedCards['mechanical'] ? 'flipped' : ''}`}>
            <div className="flipcard-inner">
              <div className="flipcard-front">
                <button className="flip-btn" onClick={() => toggleFlip('mechanical')} title="Show info">?</button>
                <h3 className="column-title">Mechanical</h3>
                <div className="mechanical-content-wrapper">
                  <div className="factsheet-card__image mechanical-image-wrapper">
                    {mechanicalImageUrl ? (
                      <img
                        src={mechanicalImageUrl}
                        alt="Cell visualization"
                        loading="lazy"
                      />
                    ) : mechanicalImageError ? (
                      <div className="factsheet-card__image-placeholder">Image not available</div>
                    ) : (
                      <div className="factsheet-card__image-placeholder">Loading...</div>
                    )}
                  </div>
                  <div className="spec-list-compact spec-list-minimal">
                    <div className="spec-row">
                      <span className="spec-label">Format</span>
                      <span className="spec-value"><span className="value-num">{combinedFormat}</span></span>
                    </div>
                    {mech.dimensions_text && (
                      <div className="spec-row">
                        <span className="spec-label">Size</span>
                        <span className="spec-value"><span className="value-num">{mech.dimensions_text}</span></span>
                      </div>
                    )}
                    <div className="spec-row">
                      <span className="spec-label">Weight</span>
                      <span className="spec-value">
                        {mech.mass_g ? (
                          <><span className="value-num">{formatValue(mech.mass_g, 'g')}</span><span className="value-unit">g</span></>
                        ) : '–'}
                      </span>
                    </div>
                    <div className="spec-row">
                      <span className="spec-label">Volume</span>
                      <span className="spec-value">
                        {mech.vol_cell_cm3 ? (
                          <><span className="value-num">{formatValue(mech.vol_cell_cm3, 'cm³')}</span><span className="value-unit">cm³</span></>
                        ) : '–'}
                      </span>
                    </div>
                    {mech.area_cm2 && (
                      <div className="spec-row">
                        <span className="spec-label">Surface</span>
                        <span className="spec-value">
                          <><span className="value-num">{formatValue(mech.area_cm2, 'cm²')}</span><span className="value-unit">cm²</span></>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flipcard-back">
                <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('mechanical')} title="Close">×</button>
                <h3 className="column-title">{FACTSHEET.mechanical.title}</h3>
                <div className="flip-content">
                  <p>{FACTSHEET.mechanical.text}</p>
                  <ul>
                    {FACTSHEET.mechanical.items.map(item => (
                      <li key={item.label}><strong>{item.label}:</strong> {item.description}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Column 3: Chemical + Quality (Stacked) */}
          <div className="key-spec-column-stacked">
            {/* Chemical Card */}
            <div className={`key-spec-card flipcard ${flippedCards['chemical'] ? 'flipped' : ''}`}>
              <div className="flipcard-inner flipcard-inner-half">
                <div className="flipcard-front">
                  <button className="flip-btn" onClick={() => toggleFlip('chemical')} title="Show info">?</button>
                  <h3 className="column-title">Chemical</h3>
                  <ChemistryDiagram
                    anode={chem.anode}
                    cathode={chem.cathode}
                    ionLabel={ionLabel}
                  />
                </div>
                <div className="flipcard-back">
                  <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('chemical')} title="Close">×</button>
                  <h3 className="column-title">{FACTSHEET.chemical.title}</h3>
                  <div className="flip-content">
                    <p>{renderDefinition(FACTSHEET.chemical.intro, cellData, globalStats)}</p>
                    <p style="margin-top: 12px; font-size: 12px; line-height: 1.5;">
                      {FACTSHEET.chemical.text}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quality Card */}
            <div className={`key-spec-card flipcard ${flippedCards['quality'] ? 'flipped' : ''}`}>
              <div className="flipcard-inner flipcard-inner-half">
                <div className="flipcard-front">
                  <button className="flip-btn" onClick={() => toggleFlip('quality')} title="Show info">?</button>
                  <h3 className="column-title">Quality</h3>
                  <div className="quality-badges">
                    {(() => {
                      // Initial Aging: CAP_loss_rel (relative capacity change as fraction)
                      // Negative = capacity gain (improvement), Positive = capacity loss
                      const initialAgingRel = aging?.CAP_loss_rel ?? 0
                      const initialAgingPct = initialAgingRel * 100

                      // Variation: CAP_spread_std_rel (standard deviation as fraction)
                      const variationRel = batch?.CAP_spread_std_rel || 0
                      const variationPct = (variationRel * 100) // Convert fraction to percent

                      // Variation quality assessment
                      let variationColor = '#4caf50' // excellent
                      if (variationPct > 10.0) {
                        variationColor = '#f44336' // poor
                      } else if (variationPct > 5.0) {
                        variationColor = '#ffc107' // average
                      } else if (variationPct > 2.0) {
                        variationColor = '#0d7c99' // good
                      }

                      // Initial aging quality assessment
                      // Negative values (capacity gain) are always excellent (green)
                      // Positive values (capacity loss) use graded colors
                      let agingColor = '#4caf50' // excellent (for negative/gain values)
                      if (initialAgingPct > 0) {
                        // Positive = capacity loss
                        if (initialAgingPct > 5.0) {
                          agingColor = '#f44336' // poor
                        } else if (initialAgingPct > 3.0) {
                          agingColor = '#ffc107' // average
                        } else if (initialAgingPct > 1.0) {
                          agingColor = '#0d7c99' // good
                        }
                      }

                      // Format display: only show minus sign for negative values (capacity gain)
                      // Positive values (capacity loss) show without plus sign
                      const agingDisplay = initialAgingPct < 0
                        ? `${initialAgingPct.toFixed(2)}`          // Negative = capacity gain (shows minus)
                        : initialAgingPct > 0
                        ? `${initialAgingPct.toFixed(2)}`          // Positive = capacity loss (no plus sign)
                        : `0.00`                                   // Exactly 0

                      return (
                        <>
                          <div className="spec-row">
                            <span className="spec-label">Cell-to-Cell Variation</span>
                            <span className="spec-value" style={`color: ${variationColor};`}>
                              <span className="value-num">{variationPct.toFixed(2)}</span><span className="value-unit">%</span>
                            </span>
                          </div>
                          <div className="spec-row">
                            <span className="spec-label">Post-Formation Loss</span>
                            <span className="spec-value" style={`color: ${agingColor};`}>
                              <span className="value-num">{agingDisplay}</span><span className="value-unit">%</span>
                            </span>
                          </div>
                        </>
                      )
                    })()}
                </div>
              </div>
              <div className="flipcard-back">
                <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('quality')} title="Close">×</button>
                <h3 className="column-title">{FACTSHEET.quality.title}</h3>
                  <div className="flip-content">
                    <p style="font-weight: 600; margin-bottom: 8px;">{FACTSHEET.quality.batchVariation.title}</p>
                    <p style="font-size: 12px; line-height: 1.5; margin-bottom: 12px;">
                      {renderDefinition(FACTSHEET.quality.batchVariation.text, cellData, globalStats)}
                    </p>
                    <p style="font-weight: 600; margin-bottom: 8px; margin-top: 16px;">{FACTSHEET.quality.initialAging.title}</p>
                    <p style="font-size: 12px; line-height: 1.5;">
                      {renderDefinition(FACTSHEET.quality.initialAging.text, cellData, globalStats)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== SECTION 2: PERFORMANCE CHARACTERISTICS ========== */}
      <div className="fact-sheet-section fact-sheet-performance">
        <div className="performance-grid">
          {/* Left Column: Electrical + Thermal stacked */}
          <div className="performance-left-column">
            {/* Electrical */}
            <div className={`performance-column electrical-column flipcard ${flippedCards['electrical'] ? 'flipped' : ''}`}>
              <div className="flipcard-inner">
                <div className="flipcard-front">
                  <button className="flip-btn" onClick={() => toggleFlip('electrical')} title="Show info">?</button>
                  <h3 className="column-title">Electrical</h3>
                  <div className="spec-list-minimal">
                    {(() => {
                      // Mass and volume for calculations
                      const m_kg = mech.mass_g ? mech.mass_g / 1000 : null
                      const vol_L = mech.vol_cell_cm3 ? mech.vol_cell_cm3 / 1000 : null

                      // Energy C/10
                      const energy_c10 = elec.E_c10_Wh || (elec.W_C10 ? elec.W_C10 / 3600 : null)

                      // Power: Standard (dc_25), Continuous (cont), Peak
                      const power_standard = elec.P_dc_25_W || null
                      const power_continuous = elec.P_cont_W || elec.P_cont || null
                      const power_peak = elec.P_peak_W || elec.P_peak || null

                      // Capacity: C/10 and Nominal
                      const capacity_c10 = elec.C_c10_Ah || (elec.C_C10 ? elec.C_C10 / 3600 : null)
                      const capacity_nominal = elec.C_nom_Ah || capacity_c10

                      // Current: Standard (I_dc_25), Continuous (I_cont), Peak
                      const current_standard = elec.I_dc_25_A || null
                      const current_continuous = elec.I_cont_A || elec.I_cont || null
                      const current_peak = elec.I_peak_A || elec.I_peak || null

                      // Energy Density
                      const energy_grav = elec.E_grav_Wh_per_kg || (elec.W_C10_grav ? elec.W_C10_grav / 3600 : null)
                      const energy_vol = elec.E_vol_Wh_per_L || (elec.W_C10_vol ? elec.W_C10_vol / 3.6e6 : null)

                      // Power Density
                      const power_grav = elec.P_grav_W_per_kg || elec.P_peak_grav || null
                      const power_vol = elec.P_vol_W_per_L || (elec.P_peak_vol ? elec.P_peak_vol / 1000 : null)

                      return (
                        <>
                          {/* Energy */}
                          <div className="spec-group">
                            <span className="spec-group-label">Energy</span>
                            <div className="spec-row">
                              <span className="spec-label">C/10</span>
                              <span className="spec-value">
                                {energy_c10 ? (
                                  <><span className="value-num">{formatValue(energy_c10, 'Wh')}</span><span className="value-unit">Wh</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>

                          {/* Power */}
                          <div className="spec-group">
                            <span className="spec-group-label">Power</span>
                            <div className="spec-row">
                              <span className="spec-label">Standard</span>
                              <span className="spec-value">
                                {power_standard ? (
                                  <><span className="value-num">{formatValue(power_standard, 'W')}</span><span className="value-unit">W</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Continuous</span>
                              <span className="spec-value">
                                {power_continuous ? (
                                  <><span className="value-num">{formatValue(power_continuous, 'W')}</span><span className="value-unit">W</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Peak</span>
                              <span className="spec-value">
                                {power_peak ? (
                                  <><span className="value-num">{formatValue(power_peak, 'W')}</span><span className="value-unit">W</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>

                          {/* Capacity */}
                          <div className="spec-group">
                            <span className="spec-group-label">Capacity</span>
                            <div className="spec-row">
                              <span className="spec-label">C/10</span>
                              <span className="spec-value">
                                {capacity_c10 ? (
                                  <><span className="value-num">{formatValue(capacity_c10, 'Ah')}</span><span className="value-unit">Ah</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Nominal</span>
                              <span className="spec-value">
                                {capacity_nominal ? (
                                  <><span className="value-num">{formatValue(capacity_nominal, 'Ah')}</span><span className="value-unit">Ah</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>

                          {/* Current */}
                          <div className="spec-group">
                            <span className="spec-group-label">Current</span>
                            <div className="spec-row">
                              <span className="spec-label">Standard</span>
                              <span className="spec-value">
                                {current_standard ? (
                                  <><span className="value-num">{formatValue(current_standard, 'A')}</span><span className="value-unit">A</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Continuous</span>
                              <span className="spec-value">
                                {current_continuous ? (
                                  <><span className="value-num">{formatValue(current_continuous, 'A')}</span><span className="value-unit">A</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Peak</span>
                              <span className="spec-value">
                                {current_peak ? (
                                  <><span className="value-num">{formatValue(current_peak, 'A')}</span><span className="value-unit">A</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>

                          {/* Energy Density */}
                          <div className="spec-group">
                            <span className="spec-group-label">Energy Density</span>
                            <div className="spec-row">
                              <span className="spec-label">Gravimetric</span>
                              <span className="spec-value">
                                {energy_grav ? (
                                  <><span className="value-num">{formatValue(energy_grav, 'Wh/kg')}</span><span className="value-unit">Wh/kg</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Volumetric</span>
                              <span className="spec-value">
                                {energy_vol ? (
                                  <><span className="value-num">{formatValue(energy_vol, 'Wh/L')}</span><span className="value-unit">Wh/L</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>

                          {/* Power Density */}
                          <div className="spec-group">
                            <span className="spec-group-label">Power Density</span>
                            <div className="spec-row">
                              <span className="spec-label">Gravimetric</span>
                              <span className="spec-value">
                                {power_grav ? (
                                  <><span className="value-num">{formatValue(power_grav, 'W/kg')}</span><span className="value-unit">W/kg</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Volumetric</span>
                              <span className="spec-value">
                                {power_vol ? (
                                  <><span className="value-num">{formatValue(power_vol, 'W/L')}</span><span className="value-unit">W/L</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>

                          {/* Voltage */}
                          <div className="spec-group">
                            <span className="spec-group-label">Voltage</span>
                            <div className="spec-row">
                              <span className="spec-label">Minimum</span>
                              <span className="spec-value">
                                {elec.V_min ? (
                                  <><span className="value-num">{formatValue(elec.V_min, 'V')}</span><span className="value-unit">V</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Mean</span>
                              <span className="spec-value">
                                {elec.V_mean ? (
                                  <><span className="value-num">{formatValue(elec.V_mean, 'V')}</span><span className="value-unit">V</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Maximum</span>
                              <span className="spec-value">
                                {elec.V_max ? (
                                  <><span className="value-num">{formatValue(elec.V_max, 'V')}</span><span className="value-unit">V</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>

                          {/* Resistance */}
                          <div className="spec-group">
                            <span className="spec-group-label">Resistance</span>
                            <div className="spec-row">
                              <span className="spec-label">DCIR</span>
                              <span className="spec-value">
                                {elec.R_dc_mOhm ? (
                                  <><span className="value-num">{formatValue(elec.R_dc_mOhm, 'mΩ')}</span><span className="value-unit">mΩ</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">AC</span>
                              <span className="spec-value">
                                {elec.R_ac_mOhm ? (
                                  <><span className="value-num">{formatValue(elec.R_ac_mOhm, 'mΩ')}</span><span className="value-unit">mΩ</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>
                <div className="flipcard-back">
                  <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('electrical')} title="Close">×</button>
                  <h3 className="column-title">{FACTSHEET.electrical.title}</h3>
                  <div className="flip-content">
                    {/* Energy / Power / Capacity / Current - Main Conditions */}
                    <p style="font-weight: 600; margin-bottom: 6px;">{FACTSHEET.electrical.mainConditions.title}</p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 8px;">
                      {renderDefinition(FACTSHEET.electrical.mainConditions.intro, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {renderDefinition(FACTSHEET.electrical.mainConditions.c10, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {renderDefinition(FACTSHEET.electrical.mainConditions.standard, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {renderDefinition(FACTSHEET.electrical.mainConditions.continuous, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {renderDefinition(FACTSHEET.electrical.mainConditions.peak, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 10px;">
                      {FACTSHEET.electrical.mainConditions.values}
                    </p>

                    {/* Energy Density / Power Density */}
                    <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.electrical.energyDensity.title}</p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 10px;">
                      {FACTSHEET.electrical.energyDensity.text}
                    </p>

                    {/* Nominal Capacity */}
                    <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.electrical.nominalCapacity.title}</p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 10px;">
                      {FACTSHEET.electrical.nominalCapacity.text}
                    </p>

                    {/* Voltage */}
                    <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.electrical.voltage.title}</p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 10px;">
                      {renderDefinition(FACTSHEET.electrical.voltage.text, cellData, globalStats)}
                    </p>

                    {/* Resistance */}
                    <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.electrical.resistance.title}</p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {renderDefinition(FACTSHEET.electrical.resistance.pulse, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4;">
                      {FACTSHEET.electrical.resistance.ac}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Thermal */}
            <div className={`performance-column thermal-column flipcard ${flippedCards['thermal'] ? 'flipped' : ''}`}>
              <div className="flipcard-inner">
                <div className="flipcard-front">
                  <button className="flip-btn" onClick={() => toggleFlip('thermal')} title="Show info">?</button>
                  <h3 className="column-title">Thermal</h3>
                  <div className="spec-list-minimal">
                    {(() => {
                      // Use same logic as ThermalDimensionView.jsx to get correct values
                      // Heat Power: P_loss_dc_25 (Standard condition at 25°C)
                      const heatPowerDC = thermal?.P_loss_dc_25
                        ?? thermal?.P_loss_dc_25_W
                        ?? thermal?.P_loss_std
                        ?? thermal?.heat_power
                        ?? null

                      // Heat Energy: W_loss_dc_25 (Standard condition at 25°C)
                      const heatEnergyDC = thermal?.W_loss_dc_25
                        ?? thermal?.W_loss_dc_25_Wh
                        ?? thermal?.E_loss_dc_25
                        ?? thermal?.heat_energy
                        ?? null

                      // Maximum Temperature: T_max_dc_25 (Standard condition at 25°C)
                      // If not available, try delta_T_dc_25 which is temperature rise from 25°C
                      const maxTempDC = thermal?.T_max_dc_25
                        ?? thermal?.T_max_std
                        ?? thermal?.max_temperature
                        ?? (thermal?.delta_T_dc_25 ? thermal.delta_T_dc_25 + 25 : null)
                        ?? null

                      console.log('[FactSheet] Thermal values:', {
                        heatEnergyDC,
                        heatPowerDC,
                        maxTempDC,
                        thermalKeys: thermal ? Object.keys(thermal) : 'no thermal'
                      })

                      return (
                        <>
                          {/* Heat Energy */}
                          <div className="spec-group">
                            <span className="spec-group-label">Heat Energy</span>
                            <div className="spec-row">
                              <span className="spec-label">Standard</span>
                              <span className="spec-value">
                                {heatEnergyDC !== null && heatEnergyDC !== undefined ? (
                                  <><span className="value-num">{formatValue(heatEnergyDC, 'Wh')}</span><span className="value-unit">Wh</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>

                          {/* Heat Power */}
                          <div className="spec-group">
                            <span className="spec-group-label">Heat Power</span>
                            <div className="spec-row">
                              <span className="spec-label">Standard</span>
                              <span className="spec-value">
                                {heatPowerDC !== null && heatPowerDC !== undefined ? (
                                  <><span className="value-num">{formatValue(heatPowerDC, 'W')}</span><span className="value-unit">W</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>

                          {/* Maximum Temperature */}
                          <div className="spec-group">
                            <span className="spec-group-label">Maximum Temperature</span>
                            <div className="spec-row">
                              <span className="spec-label">Standard</span>
                              <span className="spec-value">
                                {maxTempDC !== null && maxTempDC !== undefined ? (
                                  <><span className="value-num">{formatValue(maxTempDC, '°C')}</span><span className="value-unit">°C</span></>
                                ) : '–'}
                              </span>
                            </div>
                            <div className="spec-row">
                              <span className="spec-label">Nominal</span>
                              <span className="spec-value">
                                <span className="value-num">65</span><span className="value-unit">°C</span>
                              </span>
                            </div>
                          </div>

                          {/* Efficiency */}
                          <div className="spec-group">
                            <span className="spec-group-label">Efficiency</span>
                            <div className="spec-row">
                              <span className="spec-label">Standard</span>
                              <span className="spec-value">
                                {elec.eta_dc_pct ? (
                                  <><span className="value-num">{formatValue(elec.eta_dc_pct, '%')}</span><span className="value-unit">%</span></>
                                ) : '–'}
                              </span>
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>
                <div className="flipcard-back">
                  <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('thermal')} title="Close">×</button>
                  <h3 className="column-title">{FACTSHEET.thermal.title}</h3>
                  <div className="flip-content">
                    {/* Heat Energy / Heat Power - Main Conditions */}
                    <p style="font-weight: 600; margin-bottom: 6px;">{FACTSHEET.thermal.mainConditions.title}</p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 8px;">
                      {renderDefinition(FACTSHEET.thermal.mainConditions.intro, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {renderDefinition(FACTSHEET.thermal.mainConditions.c10, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {renderDefinition(FACTSHEET.thermal.mainConditions.standard, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {renderDefinition(FACTSHEET.thermal.mainConditions.continuous, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {renderDefinition(FACTSHEET.thermal.mainConditions.peak, cellData, globalStats)}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 10px;">
                      {FACTSHEET.thermal.mainConditions.values}
                    </p>

                    {/* Maximum Temperature */}
                    <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.thermal.maxTemperature.title}</p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 6px;">
                      {FACTSHEET.thermal.maxTemperature.nominal}
                    </p>
                    <p style="font-size: 11px; line-height: 1.4; margin-bottom: 10px;">
                      {FACTSHEET.thermal.maxTemperature.standard}
                    </p>

                    {/* Efficiency */}
                    <p style="font-weight: 600; margin-bottom: 6px; margin-top: 12px;">{FACTSHEET.thermal.efficiency.title}</p>
                    <p style="font-size: 11px; line-height: 1.4;">
                      {FACTSHEET.thermal.efficiency.text}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Plots */}
          <div className={`performance-column plots-column flipcard ${flippedCards['plots'] ? 'flipped' : ''}`}>
            <div className="flipcard-inner">
              <div className="flipcard-front">
                <button className="flip-btn" onClick={() => toggleFlip('plots')} title="Show info">?</button>
                <h3 className="column-title">Discharge Characteristics</h3>
                <div className="plot-wrapper">
                  <DischargeRateCharacteristics
                    cellData={cellData}
                    plotId="voltage-plot"
                    showLegend={true}
                    showTemperature={false}
                  />
                </div>
                <div className="plot-wrapper">
                  <DischargeRateCharacteristics
                    cellData={cellData}
                    plotId="temperature-plot"
                    showLegend={true}
                    showTemperature={true}
                  />
                </div>
              </div>
              <div className="flipcard-back">
                <button className="flip-btn flip-btn-back" onClick={() => toggleFlip('plots')} title="Close">×</button>
                <h3 className="column-title">{FACTSHEET.dischargePlots.title}</h3>
                <div className="flip-content">
                  <p style="font-size: 12px; line-height: 1.5;">
                    {renderDefinition(FACTSHEET.dischargePlots.text, cellData, globalStats)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
